/**
 * #490: расписание карточки «Фото прогресса» — по слотам программы.
 *
 * Было: карточка считала свой цикл — «30 дней от последнего снимка, окно 7
 * дней». Сервер же живёт по неделям ступени (EXTRA_SLOTS: недели 1, 5, 9, 13 —
 * схема согласована с продуктом 15.08.2026; #639: после тестовой недели фото
 * месяца идут на 5, 9, 13 неделе основной программы, getExtraSlotWeeks), и по
 * ним же шлёт напоминания.
 * Два расписания разъезжались, и клиент видел «Сдан», когда фото месяца уже
 * требовалось: у Андрея @WarAn38 (старт потока 24.07) слот месяца 1 открылся
 * 21.08, а карточка обещала «следующий 23 авг.» и вела в галерею вместо
 * инструкции.
 *
 * Теперь расписание одно — то, что отдаёт сервер в GET /surveys/status.
 */

export interface PhotoExtra {
  slot: string
  kind: string
  open: boolean
  claimed: boolean
  opensAt?: string | null
  closesAt?: string | null
}

export interface PhotoCycle {
  state: 'open' | 'done' | 'closed' | 'overdue'
  lastSubmittedAt: Date | null
  nextOpenAt: Date | null
  windowEndAt: Date | null
  /**
   * #557: когда закрылось последнее окно, которое клиент пропустил (ни снимка
   * в окне, ни награды). Карточка «closed» говорила только «Откроется 25 сент.»,
   * и поддержка не понимала, почему нельзя сдать фото, присланное через день
   * после закрытия окна. Теперь видно: «Закрылось 10 сент. · откроется 25 сент.».
   */
  missedWindowEndAt?: Date | null
}

const at = (iso?: string | null): Date | null => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Начало суток — чтобы сравнивать день снимка с днём открытия слота, а не с моментом. */
const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())

/**
 * @param extras   слоты из GET /surveys/status
 * @param lastPhotoAt последний снимок клиента (для подписи «Сдан <дата>»)
 * @param photoDays   дни, в которые снимки реально сданы (YYYY-MM-DD)
 */
export function photoCycleFromExtras(
  extras: PhotoExtra[] | undefined,
  lastPhotoAt: Date | null,
  photoDays: Set<string>,
  now: Date = new Date(),
): PhotoCycle | null {
  const photos = (extras ?? []).filter((e) => e.kind === 'PHOTO')
  if (photos.length === 0) return null

  const openSlot = photos.find((e) => e.open)
  if (openSlot) {
    const opensAt = at(openSlot.opensAt)
    const closesAt = at(openSlot.closesAt)
    // Снимок, сделанный в день открытия слота или позже, закрывает этот слот.
    //
    // #503: сравнивать день с точным моментом открытия нельзя. Слот открывается
    // не в полночь — эпоха ступени хранит время старта (getExtraSlotOpensAt), —
    // а день снимка приходит сюда полуночью, которая всегда раньше. Из-за этого
    // снимок, сданный в день открытия, слот не закрывал: Михаил @fominmal сдал
    // фото 28.08 в 15:02, бот зачёл его и начислил награду, а карточка осталась
    // «СДАТЬ». Поэтому обе даты приводим к началу дня.
    const opensDay = opensAt ? startOfDay(opensAt) : null
    const submittedForSlot = opensDay
      ? [...photoDays].some((k) => new Date(`${k}T00:00:00`) >= opensDay)
      : false;
    // #503: награда за слот уже в ledger — сервер считает слот закрытым, и
    // карточка обязана говорить то же самое. Снимка в приложении при этом может
    // не быть вовсе: фото, отправленное боту, живёт в чате с куратором.
    // Одного claimed мало — у стартового слота награда 0, записи в ledger нет.
    if (openSlot.claimed || submittedForSlot) {
      return { state: 'done', lastSubmittedAt: lastPhotoAt, nextOpenAt: nextOpensAfter(photos, now), windowEndAt: closesAt }
    }
    return { state: 'open', lastSubmittedAt: lastPhotoAt, nextOpenAt: opensAt, windowEndAt: closesAt }
  }

  // Открытого слота нет: показываем, когда откроется следующий.
  const next = nextOpensAfter(photos, now)
  if (next) {
    return {
      state: 'closed', lastSubmittedAt: lastPhotoAt, nextOpenAt: next, windowEndAt: null,
      missedWindowEndAt: lastMissedCloseAt(photos, photoDays, now),
    }
  }

  // Слоты ступени кончились — карточке больше нечего просить.
  return { state: 'done', lastSubmittedAt: lastPhotoAt, nextOpenAt: null, windowEndAt: null }
}

/**
 * #557: момент закрытия последнего прошедшего фото-слота, если он пропущен —
 * награды нет и ни один снимок в приложении не попал в его окно. Слот с
 * нулевой наградой (стартовый) без снимка тоже считается пропущенным.
 */
function lastMissedCloseAt(photos: PhotoExtra[], photoDays: Set<string>, now: Date): Date | null {
  const past = photos
    .map((e) => ({ e, opensAt: at(e.opensAt), closesAt: at(e.closesAt) }))
    .filter((x): x is { e: PhotoExtra; opensAt: Date; closesAt: Date } => x.opensAt != null && x.closesAt != null && x.closesAt <= now)
    .sort((a, b) => b.closesAt.getTime() - a.closesAt.getTime())
  const last = past[0]
  if (!last || last.e.claimed) return null
  const from = startOfDay(last.opensAt)
  const to = last.closesAt
  const submitted = [...photoDays].some((k) => {
    const d = new Date(`${k}T00:00:00`)
    return d >= from && d <= to
  })
  return submitted ? null : last.closesAt
}

/** Ближайшее открытие фото-слота строго в будущем. */
function nextOpensAfter(photos: PhotoExtra[], now: Date): Date | null {
  const future = photos
    .map((e) => at(e.opensAt))
    .filter((d): d is Date => d != null && d > now)
    .sort((a, b) => a.getTime() - b.getTime())
  return future[0] ?? null
}
